import type { CSSProperties } from 'react'

interface ThinkingDotsProps {
  /** Optional label rendered next to the dots — defaults to "Thinking". */
  label?: string
  /** Color of the dots. Defaults to current ink-3 muted text. */
  color?: string
  /** Diameter of each dot in px. Defaults to 4. */
  size?: number
  style?: CSSProperties
}

const ANIMATION_NAME = 'lms-thinking-dot'

/**
 * Three-dot loading indicator for AI surfaces. The dots stagger by 160 ms
 * so they form a wave instead of pulsing in unison — feels less like a
 * generic spinner, more like an LLM "thinking" affordance. Inlines the
 * keyframes once per mount; React dedupes identical `<style>` nodes inside
 * the same tree so this is cheap.
 *
 * Reduced-motion users get a static row via the global media-query rule
 * in tokens.css that flattens animation-duration to 1ms.
 */
export function ThinkingDots({
  label = 'Thinking',
  color = 'var(--text-3)',
  size = 4,
  style,
}: ThinkingDotsProps): JSX.Element {
  const dotStyle = (delay: number): CSSProperties => ({
    width: size,
    height: size,
    borderRadius: '50%',
    background: color,
    display: 'inline-block',
    animation: `${ANIMATION_NAME} 1.2s var(--motion-ease-inout) infinite`,
    animationDelay: `${delay}ms`,
  })
  return (
    <span
      data-testid="thinking-dots"
      role="status"
      aria-label={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontStyle: 'italic',
        color,
        ...style,
      }}
    >
      <style>{`
        @keyframes ${ANIMATION_NAME} {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-2px); }
        }
      `}</style>
      <span>{label}</span>
      <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
        <span aria-hidden="true" style={dotStyle(0)} />
        <span aria-hidden="true" style={dotStyle(160)} />
        <span aria-hidden="true" style={dotStyle(320)} />
      </span>
    </span>
  )
}
